import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTornado, faChartBar, faThermometerHalf, faCalendar } from '@fortawesome/free-solid-svg-icons';
import { getBackendUrl } from '../../../services/configService';
import { getFWIDangerClass } from '../../../utils/fwiClassification';

interface SpreadEventRecord {
    date: string;
    year: number;
    month: number;
    day: number;
    fwi: number;
    wind_speed: number | null;
    wind_direction: number | null;
}

interface SpreadEventWindroseProps {
    stationId: string;
    stationName?: string;
    communityName?: string;
    startYear?: number;
    endYear?: number;
    fwiThreshold?: number;
    width?: number;
    height?: number;
}

interface SpreadEventWindroseData {
    direction: string;
    count: number;
    percentage: number;
    speedRanges: Array<{
        label: string;
        count: number;
        color: string;
    }>;
}

// Wind direction mappings (exactly same as HourlyWindrose)
const DIRECTIONS = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
const SPEED_RANGES = [
    { min: 0, max: 5, color: '#00FFFF', label: '0-5 km/h' },
    { min: 6, max: 10, color: '#0000FF', label: '6-10 km/h' },
    { min: 11, max: 15, color: '#00FF00', label: '11-15 km/h' },
    { min: 16, max: 20, color: '#FFFF00', label: '16-20 km/h' },
    { min: 21, max: 25, color: '#FFA500', label: '21-25 km/h' },
    { min: 26, max: 999, color: '#FF0000', label: '>25 km/h' }
];

export const SpreadEventWindrose: React.FC<SpreadEventWindroseProps> = ({
    stationId,
    stationName,
    communityName,
    startYear = 2015,
    endYear = 2024,
    fwiThreshold = 19,
    width = 400,
    height = 400
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [windroseData, setWindroseData] = useState<SpreadEventWindroseData[]>([]);
    const [metadata, setMetadata] = useState<any>(null);

    const getDirectionFromDegrees = (degrees: number): string => {
        const normalized = ((degrees % 360) + 360) % 360;
        const index = Math.round(normalized / 22.5) % 16;
        return DIRECTIONS[index];
    };

    const getSpeedRange = (speed: number) => {
        return SPEED_RANGES.find(range => speed >= range.min && speed <= range.max) || SPEED_RANGES[0];
    };

    const generateSpreadEventWindrose = (records: SpreadEventRecord[]): SpreadEventWindroseData[] => {
        console.log('🔄 generateSpreadEventWindrose called with records:', records?.length || 0);

        const directionData: { [key: string]: { total: number; speedBuckets: { [key: string]: number } } } = {};

        // Initialize direction data
        DIRECTIONS.forEach(dir => {
            directionData[dir] = { total: 0, speedBuckets: {} };
            SPEED_RANGES.forEach(range => {
                directionData[dir].speedBuckets[range.label] = 0;
            });
        });

        // Process records
        let validRecords = 0;
        records.forEach((record, index) => {
            if (record.wind_direction !== null && record.wind_speed !== null) {
                const direction = getDirectionFromDegrees(record.wind_direction);
                const speedRange = getSpeedRange(record.wind_speed);

                // Log first few records for debugging
                if (index < 5) {
                    console.log(`🔍 Spread Event Record ${index}:`, {
                        wind_direction: record.wind_direction,
                        wind_speed: record.wind_speed,
                        fwi: record.fwi,
                        mapped_direction: direction,
                        speed_range: speedRange.label
                    });
                }

                directionData[direction].total++;
                directionData[direction].speedBuckets[speedRange.label]++;
                validRecords++;
            }
        });

        console.log('📊 Processing summary:', {
            totalRecords: records.length,
            validRecords,
            invalidRecords: records.length - validRecords,
            directionCounts: Object.entries(directionData).reduce((acc, [dir, data]) => ({ ...acc, [dir]: data.total }), {})
        });

        // Convert to windrose data format (same structure as 24h windrose)
        const totalValid = validRecords;
        const result = DIRECTIONS.map(direction => {
            const count = directionData[direction].total;
            const percentage = totalValid > 0 ? (count / totalValid) * 100 : 0;

            const speedRanges = SPEED_RANGES.map(range => ({
                label: range.label,
                count: directionData[direction].speedBuckets[range.label] || 0,
                color: range.color
            }));

            return {
                direction,
                count,
                percentage,
                speedRanges
            };
        });

        console.log('🎯 Final spread event windrose data:', result);
        console.log('📊 Non-zero directions:', result.filter(d => d.percentage > 0));

        return result;
    };

    const drawWindrose = () => {
        const canvas = canvasRef.current;
        if (!canvas || windroseData.length === 0) return;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;

        // Set canvas size for retina display
        const devicePixelRatio = window.devicePixelRatio || 1;
        canvas.width = width * devicePixelRatio;
        canvas.height = height * devicePixelRatio;
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;
        ctx.scale(devicePixelRatio, devicePixelRatio);

        // Clear canvas
        ctx.clearRect(0, 0, width, height);

        const centerX = width / 2;
        const centerY = height / 2;
        const maxRadius = Math.min(width, height) / 2 - 60;

        // Find max percentage for scaling
        const maxPercentage = Math.max(...windroseData.map(d => d.percentage));
        if (maxPercentage === 0) return;

        // Draw concentric circles (grid)
        ctx.strokeStyle = '#e0e0e0';
        ctx.lineWidth = 1;
        for (let i = 1; i <= 4; i++) {
            const radius = (maxRadius * i) / 4;
            ctx.beginPath();
            ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
            ctx.stroke();

            // Add percentage labels
            ctx.fillStyle = '#666';
            ctx.font = '10px Arial';
            ctx.textAlign = 'left';
            const percentage = ((maxPercentage * i) / 4).toFixed(1);
            ctx.fillText(`${percentage}%`, centerX + radius + 5, centerY);
        }

        // Draw direction lines and labels
        ctx.strokeStyle = '#e0e0e0';
        ctx.lineWidth = 1;
        DIRECTIONS.forEach((direction, index) => {
            const angle = (index * 22.5 - 90) * (Math.PI / 180); // Start from North (top)
            const x1 = centerX + Math.cos(angle) * 20;
            const y1 = centerY + Math.sin(angle) * 20;
            const x2 = centerX + Math.cos(angle) * maxRadius;
            const y2 = centerY + Math.sin(angle) * maxRadius;

            ctx.beginPath();
            ctx.moveTo(x1, y1);
            ctx.lineTo(x2, y2);
            ctx.stroke();

            // Direction labels
            const labelX = centerX + Math.cos(angle) * (maxRadius + 25);
            const labelY = centerY + Math.sin(angle) * (maxRadius + 25);
            ctx.fillStyle = '#333';
            ctx.font = 'bold 12px Arial';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(direction, labelX, labelY);
        });

        // Draw windrose data as arc segments (exactly same as 24h windrose)
        windroseData.forEach((data, index) => {
            if (data.count > 0) {
                const angle = (index * 22.5 - 90) * Math.PI / 180;
                const sectorWidth = 22.5 * Math.PI / 180;
                const startAngle = angle - sectorWidth / 2;
                const endAngle = angle + sectorWidth / 2;

                let cumulativeRadius = 0;

                // Draw each speed range as a stacked segment
                data.speedRanges.forEach(speedRange => {
                    if (speedRange.count > 0) {
                        const segmentPercentage = (speedRange.count / data.count) * data.percentage;
                        const segmentHeight = (segmentPercentage / maxPercentage) * maxRadius;

                        // Draw arc segment
                        ctx.fillStyle = speedRange.color;
                        ctx.beginPath();
                        ctx.arc(centerX, centerY, cumulativeRadius, startAngle, endAngle);
                        ctx.arc(centerX, centerY, cumulativeRadius + segmentHeight, endAngle, startAngle, true);
                        ctx.closePath();
                        ctx.fill();

                        // Draw border
                        ctx.strokeStyle = '#fff';
                        ctx.lineWidth = 1;
                        ctx.stroke();

                        cumulativeRadius += segmentHeight;
                    }
                });
            }
        });        // Draw center circle
        ctx.fillStyle = '#fff';
        ctx.strokeStyle = '#333';
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(centerX, centerY, 15, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();
    };

    const loadSpreadEventData = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${getBackendUrl()}/api/windrose/spread-events`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include',
                body: JSON.stringify({
                    stationId,
                    stationName,
                    communityName,
                    startYear,
                    endYear,
                    fwiThreshold
                }),
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const result = await response.json();
            console.log('🌬️ SpreadEventWindrose API Response:', result);

            if (result.success && result.data) {
                console.log('🌬️ Raw spread event records count:', result.data.spread_event_days?.length || 0);
                console.log('🌬️ Sample spread event records:', result.data.spread_event_days?.slice(0, 3) || []);

                const processedData = generateSpreadEventWindrose(result.data.spread_event_days || []);
                setWindroseData(processedData);
                setMetadata(result.data.metadata);

                console.log('🌬️ Processed spread event windrose data:', processedData);
            } else {
                throw new Error('Invalid response format');
            }

        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load spread event data');
            console.error('10-Year Windrose error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (stationId) {
            loadSpreadEventData();
        }
    }, [stationId, startYear, endYear, fwiThreshold]);

    useEffect(() => {
        if (windroseData.length > 0) {
            drawWindrose();
        }
    }, [windroseData, width, height]);

    if (loading) {
        return (
            <div className="windrose-container" style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="windrose-loading">
                    <div className="loading-spinner" style={{
                        width: '32px',
                        height: '32px',
                        border: '3px solid #f3f3f3',
                        borderTop: '3px solid #3498db',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                    }}></div>
                    <p style={{ marginTop: '12px', color: '#666' }}>Generating 10-year windrose...</p>
                </div>
                <style dangerouslySetInnerHTML={{
                    __html: `
                        @keyframes spin {
                            0% { transform: rotate(0deg); }
                            100% { transform: rotate(360deg); }
                        }
                    `
                }} />
            </div>
        );
    }

    if (error) {
        return (
            <div className="windrose-container" style={{ width, height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="windrose-error" style={{
                    padding: '20px',
                    border: '1px solid #dc3545',
                    borderRadius: '8px',
                    backgroundColor: '#f8d7da',
                    color: '#721c24',
                    textAlign: 'center',
                    maxWidth: '300px'
                }}>
                    <h4 style={{ margin: '0 0 8px 0', color: '#721c24' }}>Windrose Error</h4>
                    <p style={{ margin: '0 0 12px 0', fontSize: '14px' }}>{error}</p>
                    <button
                        onClick={loadSpreadEventData}
                        style={{
                            padding: '6px 12px',
                            backgroundColor: '#dc3545',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '12px'
                        }}
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="windrose-container" style={{ width, minHeight: height, display: 'flex', flexDirection: 'column' }}>
            <div style={{ position: 'relative', width: '100%', height: `${height}px`, flexShrink: 0 }}>
                <canvas
                    ref={canvasRef}
                    style={{
                        width: '100%',
                        height: '100%',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px'
                    }}
                />
                <div style={{
                    position: 'absolute',
                    top: '10px',
                    left: '10px',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    padding: '8px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    color: '#333',
                    border: '1px solid #ccc'
                }}>
                    <FontAwesomeIcon icon={faTornado} style={{ fontSize: '20px', marginRight: '6px' }} />
                    10-Year Spread Events (FWI ≥{fwiThreshold} — {getFWIDangerClass(fwiThreshold)!.label})
                </div>
            </div>

            {/* Speed Legend */}
            <div className="speed-legend" style={{
                fontSize: '10px',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4px',
                justifyContent: 'center',
                marginTop: '8px',
                padding: '8px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px',
                color: '#333'
            }}>
                {SPEED_RANGES.map(range => (
                    <div key={range.label} style={{ display: 'flex', alignItems: 'center', gap: '2px' }}>
                        <div style={{
                            width: '12px',
                            height: '12px',
                            backgroundColor: range.color,
                            border: '1px solid #ccc'
                        }}></div>
                        <span style={{ color: '#333' }}>{range.label}</span>
                    </div>
                ))}
            </div>

            {metadata && (
                <div className="windrose-metadata" style={{
                    fontSize: '11px',
                    color: '#666',
                    marginTop: '4px',
                    textAlign: 'center'
                }}>
                    <div><FontAwesomeIcon icon={faChartBar} style={{ fontSize: '14px', marginRight: '4px' }} />{metadata.totalSpreadEventDays} spread event days ({metadata.year_range})</div>
                    <div><FontAwesomeIcon icon={faThermometerHalf} style={{ fontSize: '14px', marginRight: '4px' }} />Average FWI: {metadata.avgFWI}</div>
                    <div><FontAwesomeIcon icon={faCalendar} style={{ fontSize: '14px', marginRight: '4px' }} />Period: {metadata.period}</div>
                </div>
            )}
        </div>
    );
};

export default SpreadEventWindrose;